import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
} from "@mui/material";
import { signInWithPopup } from "firebase/auth";
import { auth, googleProvider } from "./firebase";
import { useAuth } from "./AuthContext";
import { setUserToken } from "./authService";

export default function LoginDialog({ open, onClose }) {
  const { t } = useTranslation();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const idToken = await result.user.getIdToken();

      const response = await fetch("/auth/firebase", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idToken }),
      });

      if (!response.ok) {
        throw new Error("Login failed. Please try again.");
      }

      const data = await response.json();
      setUserToken(data.token, data.expiresIn);
      login({
        name: result.user.displayName,
        email: result.user.email,
        picture: result.user.photoURL,
      });
      onClose();
    } catch (err) {
      setError(err.message || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ textAlign: "center", pb: 0 }}>
        {t("loginDialogTitle")}
      </DialogTitle>
      <DialogContent>
        <Typography
          variant="body2"
          color="text.secondary"
          textAlign="center"
          sx={{ mb: 3, mt: 1 }}
        >
          {t("loginBenefit")}
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box display="flex" justifyContent="center">
          <Button
            variant="outlined"
            onClick={handleGoogleLogin}
            disabled={loading}
            startIcon={
              loading ? (
                <CircularProgress size={18} />
              ) : (
                <img
                  src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                  alt="Google"
                  width={18}
                  height={18}
                />
              )
            }
            sx={{ textTransform: "none", minWidth: 220 }}
          >
            {t("loginWithGoogle")}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
